import moment from 'moment'
import { useCallback, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardColors,
  Select,
  Text,
  Label,
  Separator,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { DATE_FORMATS } from '@/utils/dateHelpers'

import {
  fetchAllocationGuidesSummary,
  postCreateAllocationGuide,
  POST_CREATE_ALLOCATION_GUIDE_KEY,
} from '@/src/actions/allocationGuideActions'
import { selectMissingAllocationGuides } from '@/src/selectors/allocationGuideSelectors'
import { getCurrentUser } from '@/src/selectors/user.selectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getFetchError } from '@/src/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'

// TODO: Migrate to V2
import { Dimmer, Loader } from 'semantic-ui-react'

export const CreateAllocationGuideCard = () => {
  const dispatch = useAppDispatch()

  const [month, setMonth] = useState<string>()
  const [loading, setLoading] = useState(false)

  const neededMonths = useReselector(selectMissingAllocationGuides)

  const user = useReselector(getCurrentUser)

  const createAllocationGuide = useCallback(async () => {
    setLoading(true)
    if (user && month) {
      await postCreateAllocationGuide(user, month)(dispatch)
      await fetchAllocationGuidesSummary()(dispatch)
      setLoading(false)
    }
  }, [dispatch, user, month])

  const options = neededMonths.map((month) => ({
    text: moment(month).format(DATE_FORMATS.MONTH_YEAR),
    value: moment(month).format(DATE_FORMATS.YEAR_MONTH),
  }))

  const error = useReselector(getFetchError, POST_CREATE_ALLOCATION_GUIDE_KEY)

  return (
    <Card color={CardColors.LEVEL_0} className="flex flex-col gap-4">
      <Text variant={TypographyStyles.H2}>Create a New Allocation Guide</Text>
      {loading && (
        <Dimmer active inverted>
          <Loader inverted>Loading new allocation guide</Loader>
        </Dimmer>
      )}
      <Text variant={TypographyStyles.BODY_MD}>
        Select a month to create an allocation guide. Income and expenses totals
        may be incomplete until monthly bookkeeping is finalized.
      </Text>
      <Text variant={TypographyStyles.BODY_MD}>
        The guide automatically updates with finalized values on the 15th of the
        following month.
      </Text>
      <Separator />
      <Label>Select Month</Label>
      <Select
        onValueChange={setMonth}
        options={options}
        placeholder="Date"
        required
        value={month}
      />
      {error && (
        <Alert
          variant="error"
          title={error.message || 'Error'}
          description="Income and Expenses are missing for the selected month."
          className="my-2"
        />
      )}

      <Button
        variant="primary"
        loading={loading}
        disabled={loading}
        onClick={createAllocationGuide}
      >
        Create Guide
      </Button>
    </Card>
  )
}
